import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight,
  Download,
  Eye,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Loader2,
  LockKeyhole,
  Mail,
  Upload,
} from 'lucide-react'

import {
  MAX_UPLOAD_BYTES,
  documentsService,
  type DealDocument,
} from '@/services/documents'
import type { LoanActivityEvent } from '@/pages/pipeline-intelligence/types'
import type { DealDossierTab } from '@/store/dealDossierStore'
import type { Trigger } from '@/types'
import {
  buildDocumentsModel,
  filterDocumentRows,
  receivedByEmail,
  type DocumentDashboardModel,
  type DocumentFilters,
  type DocumentRow,
  type DocumentStatus,
  type DocumentTab,
} from './documentsModel'
import { ingestionService, type SourceFileRef } from '@/services/ingestion'
import { parseDateValue } from '@/utils/dates'
import { documentRequirements } from '@/services/documentRequirements'
import type { DealFieldCatalogue } from '@/services/dealFile'
import type { DealCorrespondence } from '@/services/dossierContext'
import { RequiredDocumentsPanel } from './RequiredDocumentsPanel'
import { IntakePackagePanel } from './IntakePackagePanel'
import { useDossierEdit } from './editContext'
import { usePanelExpansion } from './ui'

type DocumentsPageProps = {
  loanId: string
  documents: DealDocument[]
  activity: LoanActivityEvent[]
  requirements: Trigger[]
  loading?: boolean
  error?: unknown
  /** The intake package this deal arrived on, previously its own Sources tab. */
  sourceIngestionId?: string | null
  sourceFiles?: SourceFileRef[]
  sourcePackage?: IntakePackage
  sourceLoading?: boolean
  /** Every file that ever arrived on this deal, and the message it came on. */
  correspondence?: DealCorrespondence | null
  /** Opens a stored original in the source viewer, which previews it in place. */
  onOpenSource?: (sha256: string) => void
  onGoto?: (tab: DealDossierTab) => void
  /** The deal field catalogue: names the intake Field Mapping rows. */
  catalogue?: DealFieldCatalogue
}

type IntakePackage = Awaited<ReturnType<typeof ingestionService.getById>> | undefined

const STATUS_STYLE: Record<DocumentStatus, string> = {
  unclassified: 'border-stone-600/40 bg-stone-500/[0.06] text-stone-400',
  received: 'border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-300',
  pending: 'border-amber-500/30 bg-amber-500/[0.07] text-amber-300',
  expired: 'border-red-500/30 bg-red-500/[0.06] text-red-300',
  approved: 'border-sky-500/30 bg-sky-500/[0.06] text-sky-300',
  rejected: 'border-red-500/30 bg-red-500/[0.06] text-red-300',
}

const TABS: { id: DocumentTab; label: string }[] = [
  { id: 'all', label: 'All Documents' },
  { id: 'required', label: 'Required' },
  { id: 'unclassified', label: 'Not reviewed' },
  { id: 'received', label: 'Received' },
  { id: 'pending', label: 'Pending' },
  { id: 'expired', label: 'Expired' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
]

const EMPTY_FILTERS: DocumentFilters = {
  tab: 'all',
  search: '',
  category: '',
  documentType: '',
  party: '',
  status: '',
}

function humanDate(value: string | null | undefined, withTime = false): string {
  if (!value) return '—'
  // Effective and expiration dates are calendar days, not instants.
  const date = parseDateValue(value)
  if (!date) return value
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  })
}

function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function titleCase(value: string): string {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter: string) => letter.toUpperCase())
}

function errorDetail(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

function documentIcon(document: DealDocument, size = 13) {
  const contentType = document.contentType?.toLowerCase() ?? ''
  const name = document.filename.toLowerCase()
  if (contentType.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(name)) {
    return <FileImage size={size} className="text-sky-300" />
  }
  if (contentType === 'message/rfc822' || /\.eml$/.test(name)) {
    return <Mail size={size} className="text-blue-400" />
  }
  if (/\.(xlsx?|csv)$/.test(name)) return <FileSpreadsheet size={size} className="text-emerald-300" />
  if (/\.(zip|rar|7z)$/.test(name)) return <FileArchive size={size} className="text-violet-300" />
  return <FileText size={size} className="text-red-300" />
}

function Surface({
  title,
  action,
  className = '',
  bodyClassName = 'p-3',
  children,
}: {
  title: string
  action?: ReactNode
  className?: string
  bodyClassName?: string
  children: ReactNode
}) {
  const expand = usePanelExpansion(title)
  return (
    // Named, so each panel is a landmark a reader can jump to rather than an
    // anonymous box — a bare <section> exposes no region at all.
    <section aria-label={title} className={`dossier-panel min-w-0 overflow-hidden rounded-lg border border-amber-400/15 bg-black/30 ${className}`}>
      <div className="flex min-h-9 shrink-0 items-center justify-between gap-3 border-b border-stone-800/80 px-3 py-2">
        <h3 className="min-w-0 truncate font-mono text-[9px] uppercase tracking-[0.16em] text-stone-300">
          {expand.trigger(title)}
        </h3>
        {action ? <span className="flex shrink-0 items-center gap-3">{action}</span> : null}
      </div>
      <div className={bodyClassName}>{children}</div>
      {expand.modal(children)}
    </section>
  )
}

function EmptyState({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-24 flex-col items-center justify-center gap-2 px-4 py-6 text-center text-[10px] leading-relaxed text-stone-600">
      {icon}
      <p className="max-w-md">{children}</p>
    </div>
  )
}

function PanelLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1 text-[9px] text-amber-300 hover:text-amber-200">
      {label} <ChevronRight size={10} />
    </button>
  )
}

function StatusPill({ status }: { status: DocumentStatus }) {
  return (
    <span
      className={`inline-flex rounded border px-1.5 py-0.5 text-[7.5px] uppercase tracking-wide ${STATUS_STYLE[status]}`}
      title={status === 'unclassified' ? 'No review status is recorded for this file yet. Its category says what it is.' : undefined}
    >
      {status === 'unclassified' ? 'Not reviewed' : titleCase(status)}
    </span>
  )
}

/** Record that a person reviewed a stored document, or take the review back.
 *  Only a file in the vault has a record to write to; an intake original that
 *  was never filed shows its pill alone. Who and when are recorded server-side. */
function ReviewAction({
  row,
  canReview,
  onReview,
  busy,
}: {
  row: DocumentRow
  canReview: boolean
  onReview?: (row: DocumentRow, status: 'received' | null) => void
  busy: boolean
}) {
  if (!onReview || row.origin !== 'vault') return null
  const recordedByPerson = row.statusRecorded && Boolean(row.reviewedBy)
  if (row.status !== 'unclassified' && !recordedByPerson) return null
  const title = !canReview
    ? 'Unlock the dossier to record a review'
    : recordedByPerson
      ? `Reviewed by ${row.reviewedBy}${row.reviewedOn ? ` on ${humanDate(row.reviewedOn)}` : ''} - take the review back`
      : 'Record that you reviewed this file (shown as Received)'
  return (
    <button
      type="button"
      disabled={!canReview || busy}
      title={title}
      onClick={(event) => {
        event.stopPropagation()
        onReview(row, recordedByPerson ? null : 'received')
      }}
      className="mx-auto mt-0.5 block text-[7.5px] text-amber-300 hover:text-amber-200 disabled:text-stone-600"
    >
      {busy ? 'Saving...' : recordedByPerson ? 'Undo' : 'Mark reviewed'}
    </button>
  )
}

type RecentItem = { key: string; title: string; subtitle: string; at: string | null; icon: ReactNode }

function recentDocumentActivity(model: DocumentDashboardModel, activity: LoanActivityEvent[]): RecentItem[] {
  const events = activity
    .filter((event) => /document|file|upload/i.test(event.eventType))
    .map((event): RecentItem => {
      const filename = ['filename', 'document_name', 'documentName', 'file_name', 'fileName']
        .map((key) => event.details[key])
        .find((value): value is string => typeof value === 'string' && Boolean(value.trim()))
      return {
        key: `event-${event.id}`,
        title: filename ?? titleCase(event.eventType),
        subtitle: event.userEmail ? `${titleCase(event.eventType)} by ${event.userEmail}` : titleCase(event.eventType),
        at: event.createdAt,
        icon: <FileText size={11} className="text-amber-300" />,
      }
    })
  const uploads = model.allVersions.map((document): RecentItem => ({
    key: `document-${document.id}`,
    title: document.filename,
    subtitle: receivedByEmail(document)
      ? 'Received by email'
      : document.uploadedBy ? `Uploaded by ${document.uploadedBy}` : 'Uploader not recorded',
    at: document.createdAt,
    icon: documentIcon(document, 11),
  }))
  // Nobody uploaded an intake original, so "uploader not recorded" would be
  // the wrong sentence for it — it arrived, and that is what it says.
  const arrivals = model.rows
    .filter((row) => row.origin === 'intake')
    .map((row): RecentItem => ({
      key: `intake-${row.document.id}`,
      title: row.document.filename,
      subtitle: row.source ?? 'Received by email',
      at: row.document.createdAt,
      icon: documentIcon(row.document, 11),
    }))
  const seen = new Set<string>()
  return [...events, ...uploads, ...arrivals]
    .sort((a, b) => new Date(b.at ?? 0).getTime() - new Date(a.at ?? 0).getTime())
    .filter((item) => {
      const key = `${item.title.toLowerCase()}|${item.at ?? ''}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, 3)
}

function RecentActivity({ model, activity, onGoto }: { model: DocumentDashboardModel; activity: LoanActivityEvent[]; onGoto?: (tab: DealDossierTab) => void }) {
  const items = recentDocumentActivity(model, activity)
  return (
    <div>
      {items.length ? (
        <div className="divide-y divide-stone-900">
          {items.map((item) => (
            <div key={item.key} className="grid grid-cols-[16px_minmax(0,1fr)_auto] gap-2 py-2 text-[9px]">
              <span className="mt-0.5">{item.icon}</span>
              <div className="min-w-0">
                <div className="truncate text-stone-300" title={item.title}>{item.title}</div>
                <div className="truncate text-[8px] text-stone-600" title={item.subtitle}>{item.subtitle}</div>
              </div>
              <span className="whitespace-nowrap text-right text-[8px] text-stone-600">{humanDate(item.at, true)}</span>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState>No document activity is recorded for this deal.</EmptyState>
      )}
      {onGoto ? <div className="mt-1 flex justify-end border-t border-stone-800 pt-2"><PanelLink label="View all activity" onClick={() => onGoto('audit')} /></div> : null}
    </div>
  )
}

function DocumentTable({
  rows,
  selectedId,
  onSelect,
  onDownload,
  downloadingId,
  canReview = false,
  onReview,
  reviewingId = null,
}: {
  rows: DocumentRow[]
  selectedId: string | null
  onSelect: (id: string) => void
  onDownload: (row: DocumentRow) => void
  downloadingId: string | null
  /** The dossier is unlocked for editing: a person may record a review. */
  canReview?: boolean
  /** Record a review (`received`) or withdraw one (`null`). */
  onReview?: (row: DocumentRow, status: 'received' | null) => void
  reviewingId?: string | null
}) {
  if (!rows.length) {
    return <EmptyState icon={<FolderOpen size={19} className="text-stone-700" />}>No documents match the current filters.</EmptyState>
  }
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      {/* Nine columns that fit the Documents panel at 1920 (924px of room) with no sideways scroll: Version
          and Actions were pushed past the panel edge and their headers cut. Narrower viewports still scroll
          the table inside its own box. */}
      <table className="w-full min-w-[900px] table-fixed border-collapse text-left">
        <thead className="bg-black/30 font-mono text-[7.5px] uppercase tracking-wider text-stone-600">
          <tr>
            <th className="w-[24%] border-b border-stone-800 px-3 py-2 font-normal">Document name</th>
            <th className="w-[10%] border-b border-stone-800 px-2 py-2 font-normal">Category</th>
            <th className="w-[11%] border-b border-stone-800 px-2 py-2 font-normal">File type</th>
            <th className="w-[10%] border-b border-stone-800 px-2 py-2 font-normal">Party</th>
            <th className="w-[11%] border-b border-stone-800 px-2 py-2 font-normal">Status</th>
            <th className="w-[11%] border-b border-stone-800 px-2 py-2 font-normal">Uploaded by</th>
            <th className="w-[10%] border-b border-stone-800 px-2 py-2 font-normal">Uploaded on</th>
            <th className="w-[7%] border-b border-stone-800 px-2 py-2 font-normal">Version</th>
            <th className="w-[6%] border-b border-stone-800 px-2 py-2 font-normal">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const selected = row.document.id === selectedId
            return (
              <tr
                key={row.document.id}
                data-testid={`document-row-${row.document.id}`}
                onClick={() => onSelect(row.document.id)}
                className={`cursor-pointer align-middle transition-colors ${selected ? 'bg-amber-400/[0.07]' : 'hover:bg-amber-400/[0.025]'}`}
              >
                <td className="border-b border-stone-900 px-3 py-2">
                  <div className="flex min-w-0 items-center justify-center gap-2">
                    <span className="shrink-0">{documentIcon(row.document)}</span>
                    <div className="min-w-0">
                      <div className="truncate text-[9.5px] text-stone-200" title={row.document.filename}>{row.document.filename}</div>
                      {row.required ? <div className="text-[7.5px] text-amber-300">Required document</div> : null}
                      {row.origin === 'intake' && row.available ? (
                        <div className="text-[7.5px] text-sky-300/80">Intake original · immutable</div>
                      ) : null}
                      {row.available ? null : (
                        <div className="text-[7.5px] text-amber-500/80" title="This file arrived on the deal, but its contents were not stored and cannot be opened.">
                          Arrived · file not stored
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="truncate border-b border-stone-900 px-2 py-2 text-[9px] text-stone-400">{titleCase(row.document.category || 'general')}</td>
                <td className="truncate border-b border-stone-900 px-2 py-2 text-[9px] text-stone-500">{row.documentType ?? '—'}</td>
                <td className="truncate border-b border-stone-900 px-2 py-2 text-[9px] text-stone-500">{row.party ?? '—'}</td>
                <td className="border-b border-stone-900 px-2 py-2">
                  <StatusPill status={row.status} />
                  <ReviewAction row={row} canReview={canReview} onReview={onReview} busy={reviewingId === row.document.id} />
                </td>
                <td className="truncate border-b border-stone-900 px-2 py-2 text-[9px] text-stone-500">
                  {receivedByEmail(row.document) ? 'Received by email' : row.document.uploadedBy ?? '—'}
                </td>
                <td className="border-b border-stone-900 px-2 py-2 text-[9px] text-stone-500">{humanDate(row.document.createdAt)}</td>
                <td className="border-b border-stone-900 px-2 py-2 font-mono text-[9px] text-stone-400">
                  {row.origin === 'intake' ? '—' : `v${row.document.version}`}
                </td>
                <td className="border-b border-stone-900 px-2 py-2">
                  <button
                    type="button"
                    aria-label={row.available ? `Download ${row.document.filename}` : `${row.document.filename} was not stored and cannot be downloaded`}
                    disabled={downloadingId === row.document.id || !row.available}
                    title={row.available ? undefined : 'Its contents were not stored, so there is nothing to download.'}
                    onClick={(event) => { event.stopPropagation(); onDownload(row) }}
                    className="text-stone-500 hover:text-amber-300 disabled:opacity-50 disabled:hover:text-stone-500"
                  >
                    {downloadingId === row.document.id ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-2 py-0.5 text-[8.5px]">
      <span className="text-stone-600">{label}</span>
      <span className="min-w-0 break-words text-stone-300">{value || '—'}</span>
    </div>
  )
}

/** The selected file's recorded metadata. Its header carries the two actions
 *  the old Document Details card held, so opening and downloading the
 *  selected file still work from here and from each table row. */
function DocumentMetadata({
  row,
  onView,
  onDownload,
  previewing,
  downloading,
}: {
  row: DocumentRow | null
  onView: (row: DocumentRow) => void
  onDownload: (row: DocumentRow) => void
  previewing: boolean
  downloading: boolean
}) {
  return (
    <Surface
      title="Document Metadata"
      className="flex h-full min-h-0 flex-col"
      bodyClassName="min-h-0 flex-1 overflow-y-auto p-3"
      action={
        row ? (
          <>
            <button type="button" onClick={() => onView(row)} disabled={previewing} className="inline-flex items-center gap-1 text-[8.5px] text-amber-200 hover:text-amber-100 disabled:opacity-50">
              {previewing ? <Loader2 size={9} className="animate-spin" /> : <Eye size={9} />} View document
            </button>
            <button type="button" onClick={() => onDownload(row)} disabled={downloading} className="inline-flex items-center gap-1 text-[8.5px] text-stone-400 hover:text-stone-200 disabled:opacity-50">
              <Download size={9} /> Download
            </button>
          </>
        ) : undefined
      }
    >
      {row ? (
        <>
          <div className="mb-1.5 flex min-w-0 items-center gap-1.5">
            <span className="shrink-0">{documentIcon(row.document)}</span>
            <span data-testid="document-detail-name" className="truncate text-[9.5px] font-semibold text-stone-100" title={row.document.filename}>
              {row.document.filename}
            </span>
          </div>
          <DetailRow label="File type" value={row.documentType ?? '—'} />
          <DetailRow label="Effective Date" value={humanDate(row.effectiveDate)} />
          <DetailRow label="Expiration Date" value={humanDate(row.expirationDate)} />
          <DetailRow label="Source" value={row.source ?? '—'} />
          <DetailRow
            label="File Size"
            value={row.document.sizeBytes > 0 ? humanSize(row.document.sizeBytes) : '—'}
          />
          <DetailRow label="Pages" value={row.pages?.toLocaleString() ?? '—'} />
          <DetailRow label="Content Type" value={row.document.contentType ?? '—'} />
          <DetailRow label="Extraction" value={row.document.extractionConfidence == null ? '—' : `${Math.round(row.document.extractionConfidence * 100)}% confidence`} />
          <DetailRow label="Checksum" value={row.document.checksumSha256 ? <span className="font-mono text-[7.5px]">{row.document.checksumSha256}</span> : '—'} />
          {row.document.notes ? <DetailRow label="Notes" value={row.document.notes} /> : null}
          {row.tags.length ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {row.tags.map((tag) => <span key={tag} className="rounded border border-stone-700 px-1.5 py-0.5 text-[7.5px] text-stone-400">{tag}</span>)}
            </div>
          ) : null}
        </>
      ) : (
        <EmptyState icon={<FileText size={18} className="text-stone-700" />}>Select a document to inspect its recorded details.</EmptyState>
      )}
    </Surface>
  )
}
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function DocumentsPage({
  loanId,
  documents,
  activity,
  requirements,
  loading = false,
  error,
  sourceIngestionId,
  sourceFiles = [],
  sourcePackage,
  sourceLoading = false,
  correspondence,
  onOpenSource,
  onGoto,
  catalogue,
}: DocumentsPageProps) {
  const { unlocked } = useDossierEdit()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [filters, setFilters] = useState<DocumentFilters>(EMPTY_FILTERS)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [previewingId, setPreviewingId] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [reviewError, setReviewError] = useState<string | null>(null)

  // Every file that ever arrived on this deal, from every message in the
  // conversation — not just the record the deal was created from. Returning a
  // stable `undefined` when there are none keeps the model memo from
  // recomputing on every render while correspondence is still loading.
  const intake = useMemo(
    () => (correspondence?.files.length ? { files: correspondence.files } : undefined),
    [correspondence],
  )

  // The checklist the panel beside the table reads (same query, same cache),
  // so the table's Required tab counts the files it accepted.
  const checklist = useQuery({
    queryKey: ['loan', loanId, 'document-requirements'],
    queryFn: () => documentRequirements.list(loanId as string),
    enabled: Boolean(loanId),
    staleTime: 30_000,
  })
  const checklistRequiredFiles = useMemo(
    () => new Set(
      (checklist.data?.items ?? [])
        .filter((item) => item.required && item.status === 'received')
        .flatMap((item) => item.satisfiedBy.map((evidence) => evidence.filename)),
    ),
    [checklist.data],
  )
  const model = useMemo(
    () => buildDocumentsModel(documents, requirements, intake, checklistRequiredFiles),
    [documents, requirements, intake, checklistRequiredFiles],
  )
  const rows = useMemo(() => filterDocumentRows(model.rows, filters), [model.rows, filters])
  const selected = rows.find((row) => row.document.id === selectedId) ?? rows[0] ?? null

  const upload = useMutation({
    // Sequential rather than parallel: the server versions a repeat filename by
    // counting what is already stored, so concurrent uploads of the same name
    // would race and both land as version 1.
    mutationFn: async (files: File[]) => {
      let last: DealDocument | null = null
      for (const file of files) last = await documentsService.upload(loanId, file)
      return last
    },
    onSuccess: async (document) => {
      setUploadError(null)
      if (document) setSelectedId(document.id)
      await queryClient.invalidateQueries({ queryKey: ['loan', loanId, 'documents'] })
    },
    onError: (uploadFailure) => setUploadError(errorDetail(uploadFailure, 'The document could not be uploaded.')),
  })

  const review = useMutation({
    mutationFn: ({ row, status }: { row: DocumentRow; status: 'received' | null }) =>
      documentsService.setReviewStatus(row.document.id, status),
    onSuccess: async () => {
      setReviewError(null)
      await queryClient.invalidateQueries({ queryKey: ['loan', loanId, 'documents'] })
    },
    onError: (failure) => setReviewError(errorDetail(failure, 'The review could not be recorded.')),
  })

  const handleUpload = (files: File[]) => {
    const tooBig = files.find((file) => file.size > MAX_UPLOAD_BYTES)
    if (tooBig) {
      setUploadError(
        `${tooBig.name} exceeds the ${MAX_UPLOAD_BYTES / 1024 / 1024} MB upload limit.`,
      )
      return
    }
    if (files.length > 0) upload.mutate(files)
  }

  const handleDownload = async (document: DealDocument) => {
    setDownloadingId(document.id)
    setDownloadError(null)
    try {
      const blob = await documentsService.download(document.id)
      downloadBlob(blob, document.filename)
    } catch (downloadFailure) {
      setDownloadError(errorDetail(downloadFailure, 'The stored document could not be downloaded.'))
    } finally {
      setDownloadingId(null)
    }
  }

  const handleView = async (document: DealDocument) => {
    setPreviewingId(document.id)
    setDownloadError(null)
    try {
      const blob = await documentsService.download(document.id)
      const url = URL.createObjectURL(blob)
      const preview = window.open(url, '_blank')
      if (!preview) {
        URL.revokeObjectURL(url)
        throw new Error('The browser blocked the document preview. Allow pop-ups and try again.')
      }
      preview.opener = null
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (previewFailure) {
      setDownloadError(errorDetail(previewFailure, 'The stored document could not be previewed.'))
    } finally {
      setPreviewingId(null)
    }
  }

  /** An intake original is immutable and sha-addressed, so it opens in the
   *  source viewer — which renders the email, the PDF and each sheet of a
   *  workbook in place — rather than being fetched by document id. */
  const openRow = (row: DocumentRow) => {
    // Nothing was stored, so both the source viewer and the vault fetch would
    // fail. The row states that; opening it must not claim otherwise.
    if (!row.available) return
    if (row.origin === 'intake' && row.sourceSha256) {
      onOpenSource?.(row.sourceSha256)
      return
    }
    void handleView(row.document)
  }

  const downloadRow = async (row: DocumentRow) => {
    if (!row.available) return
    // A deal has one intake record per message, and a file is addressed
    // through its own — not through whichever record the deal was created
    // from, which would 404 for anything that arrived in a reply.
    const ingestionId = row.sourceIngestionId ?? sourceIngestionId
    if (row.origin !== 'intake' || !row.sourceSha256 || !ingestionId) {
      await handleDownload(row.document)
      return
    }
    setDownloadingId(row.document.id)
    setDownloadError(null)
    try {
      const blob = await ingestionService.getSourceBlob(ingestionId, row.sourceSha256)
      downloadBlob(blob, row.document.filename)
    } catch (downloadFailure) {
      setDownloadError(errorDetail(downloadFailure, 'The stored original could not be downloaded.'))
    } finally {
      setDownloadingId(null)
    }
  }

  if (loading) {
    return <div className="flex h-full items-center justify-center gap-2 text-[11px] text-stone-500"><Loader2 size={14} className="animate-spin" /> Loading documents…</div>
  }
  if (error) {
    return <div className="flex h-full items-center justify-center px-6 text-center text-[11px] text-red-300">{errorDetail(error, 'Documents could not be loaded for this deal.')}</div>
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden pr-1">
      <input
        ref={inputRef}
        type="file"
        multiple
        aria-label="Upload deal document"
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          event.target.value = ''
          handleUpload(files)
        }}
      />
      <div className="grid min-h-0 flex-1 gap-2 xl:grid-cols-12 xl:grid-rows-[minmax(0,1fr)_14rem]">
        {/* The package the deal arrived in, running the full height of the
            column. It was a short panel below four counters that restated what
            the table beneath them already said; the evidence is the thing worth
            the space. */}
        <Surface
          title="Intake Package"
          className="flex min-h-0 flex-col xl:col-span-3 xl:row-span-2"
          bodyClassName="min-h-0 flex-1 overflow-y-auto p-3"
        >
          <IntakePackagePanel
            sourceIngestionId={sourceIngestionId}
            sourceFiles={sourceFiles}
            pkg={sourcePackage}
            catalogue={catalogue}
            loading={sourceLoading}
            onOpen={(sha) => onOpenSource?.(sha)}
            laterFileCount={
              (correspondence?.files ?? []).filter(
                (file) => file.ingestionId && file.ingestionId !== sourceIngestionId,
              ).length
            }
          />
        </Surface>

        <Surface
          title="Documents"
          className="flex min-h-0 flex-col xl:col-span-6"
          bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
          action={
            <button
              type="button"
              disabled={!unlocked || upload.isPending}
              onClick={() => inputRef.current?.click()}
              title={unlocked ? 'Upload a document' : 'Unlock the dossier to upload a document'}
              className="inline-flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[8.5px] text-amber-200 hover:bg-amber-400/[0.06] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-600"
            >
              {upload.isPending ? <Loader2 size={9} className="animate-spin" /> : unlocked ? <Upload size={9} /> : <LockKeyhole size={9} />}
              {upload.isPending ? 'Uploading…' : 'Upload Document'}
            </button>
          }
        >
          {uploadError ? <p className="shrink-0 border-b border-stone-800 px-3 py-2 text-[9px] text-red-300">{uploadError}</p> : null}
          <div className="flex shrink-0 overflow-x-auto border-b border-stone-800 px-2" role="tablist" aria-label="Document status tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                data-testid={`document-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={filters.tab === tab.id}
                onClick={() => setFilters((current) => ({ ...current, tab: tab.id }))}
                className={`shrink-0 border-b-2 px-3 py-2 text-[8px] uppercase tracking-wide ${filters.tab === tab.id ? 'border-amber-300 text-amber-200' : 'border-transparent text-stone-600 hover:text-stone-300'}`}
              >
                {tab.label} <span className="ml-1 font-mono text-[7px] text-stone-600">{model.tabCounts[tab.id]}</span>
              </button>
            ))}
          </div>
          {downloadError ? <p className="shrink-0 border-b border-stone-800 px-3 py-2 text-[9px] text-red-300">{downloadError}</p> : null}
          {reviewError ? <p role="alert" className="shrink-0 border-b border-stone-800 px-3 py-2 text-[9px] text-red-300">{reviewError}</p> : null}
          <DocumentTable
            rows={rows}
            selectedId={selected?.document.id ?? null}
            onSelect={setSelectedId}
            onDownload={(row) => void downloadRow(row)}
            downloadingId={downloadingId}
            canReview={unlocked}
            onReview={(row, status) => review.mutate({ row, status })}
            reviewingId={review.isPending ? review.variables?.row.document.id ?? null : null}
          />
          <div className="flex shrink-0 items-center justify-between border-t border-stone-800 px-3 py-2 text-[8px] text-stone-600">
            <span>
              Showing {rows.length.toLocaleString()} of {model.totalDocuments.toLocaleString()} current documents
              {model.totalVersions !== model.totalDocuments ? (
                /* Superseded vault versions and intake originals are held too;
                   the count of what is CURRENT is not the count of what is
                   kept, and the difference is the version history. */
                <span className="text-stone-700"> · {model.totalVersions.toLocaleString()} stored files</span>
              ) : null}
            </span>
            {(filters.search || filters.category || filters.documentType || filters.party || filters.status || filters.tab !== 'all') && rows.length !== model.totalDocuments ? (
              <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} className="text-amber-300 hover:text-amber-200">Clear filters</button>
            ) : null}
          </div>
        </Surface>

        {/* Required documents run the full height of the right column. */}
        <Surface
          title="Required Documents"
          className="flex min-h-0 flex-col xl:col-span-3 xl:row-span-2"
          bodyClassName="min-h-0 flex-1 overflow-hidden p-3"
        >
          <RequiredDocumentsPanel loanId={loanId} />
        </Surface>

        {/* Under the table: what happened to the files (left) and the
            selected file's record (right). */}
        <div className="grid min-h-0 gap-2 md:grid-cols-2 xl:col-span-6 xl:col-start-4 xl:row-start-2">
          <Surface
            title="Recent Activity"
            className="flex min-h-0 flex-col"
            bodyClassName="min-h-0 flex-1 overflow-y-auto p-3"
          >
            <RecentActivity model={model} activity={activity} onGoto={onGoto} />
          </Surface>
          <DocumentMetadata
            row={selected}
            onView={(row) => openRow(row)}
            onDownload={(row) => void downloadRow(row)}
            previewing={Boolean(selected && previewingId === selected.document.id)}
            downloading={Boolean(selected && downloadingId === selected.document.id)}
          />
        </div>
      </div>
    </div>
  )
}
